import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL, API_URL_UPLOADS } from "./config";

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [customerCount, setCustomerCount] = useState(0); // New state for customer count

  useEffect(() => {
    // Fetch data from your endpoint,
    fetch(`${API_URL}/customerdetails/`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setCustomers(data);
        setFilteredCustomers(data);
      })
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  useEffect(() => {
    let filtered = [...customers];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (customer) => customer.membershipstatus === statusFilter
      );
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(
        (customer) => customer.account_category === categoryFilter
      );
    }

    // Date filter
    if (dateFrom && dateTo) {
      filtered = filtered.filter((customer) => {
        const customerDate = new Date(customer.signed_on);
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo);
        return customerDate >= fromDate && customerDate <= toDate;
      });
    }

    setFilteredCustomers(filtered);
    setCustomerCount(filtered.length); // Update customer count
  }, [statusFilter, categoryFilter, dateFrom, dateTo, customers]);

  const statuses = [
    "all",
    ...new Set(customers.map((customer) => customer.membershipstatus)),
  ];
  const categories = [
    "all",
    ...new Set(customers.map((customer) => customer.account_category)),
  ].filter(Boolean);

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        <h1 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white">
          Customer Details
        </h1>

        {/* Customer Count Card */}
        <div className="mb-6 bg-blue-100 border border-blue-300 text-blue-800 text-center p-4 rounded-lg">
          <strong>{customerCount}</strong> customers displayed
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Status
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status === "all" ? "All Statuses" : status}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Account Category
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category === "all" ? "All Categories" : category}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Date From
            </label>
            <input
              type="date"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Date To
            </label>
            <input
              type="date"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto shadow-md rounded-lg">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th className="px-6 py-3">Customer ID</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Phone</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Signed On</th>
                <th className="px-6 py-3">Rating</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr
                  key={customer.customerid}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <td className="px-6 py-4">{customer.customerid}</td>
                  <td className="px-6 py-4">{`${customer.name} ${customer.surname}`}</td>
                  <td className="px-6 py-4">{customer.email}</td>
                  <td className="px-6 py-4">{customer.phone}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        customer.membershipstatus === "Pending OTP Verification"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {customer.membershipstatus}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {customer.signed_on
                      ? format(new Date(customer.signed_on), "MMM dd, yyyy")
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4">{customer.rating || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* No results message */}
        {filteredCustomers.length === 0 && (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            No customers found matching the selected filters.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Customers;
